import React, { useState } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, TextField, Stack, Dropdown, Checkbox, Label } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import './WidgetInstanceConfig.css';

/**
 * Dialog pour configurer une instance de widget
 * @param {boolean} isOpen - Dialog ouvert ou fermé
 * @param {function} onDismiss - Callback pour fermer le dialog
 * @param {object} widgetInfo - Informations du widget depuis le catalogue
 * @param {object} instance - Instance existante à éditer (optionnel)
 * @param {function} onSave - Callback pour sauvegarder (instance) => {}
 * @param {array} availableTabs - Liste des tabs disponibles
 * @param {string} currentTabId - ID du tab actuellement actif (pour pré-sélection)
 */
const WidgetInstanceConfig = ({ isOpen, onDismiss, widgetInfo, instance, onSave, availableTabs, currentTabId }) => {
    const { t } = useTranslation();
    
    // État local pour la configuration
    const [config, setConfig] = useState({
        name: instance?.name || widgetInfo?.name || '',
        tabId: instance?.tabId || currentTabId || availableTabs[0]?.id || '',
        filters: instance?.config?.filters || {},
        params: instance?.config?.params || {},
        refreshInterval: instance?.config?.refreshInterval || 60,
        showTitle: instance?.config?.showTitle !== false,
        showBorder: instance?.config?.showBorder !== false
    });

    const handleSave = () => {
        const widgetInstance = {
            id: instance?.id || `instance-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            widgetId: widgetInfo.id,
            name: config.name || widgetInfo.name,
            tabId: config.tabId,
            config: {
                filters: config.filters,
                params: config.params,
                refreshInterval: config.refreshInterval,
                showTitle: config.showTitle,
                showBorder: config.showBorder
            },
            position: instance?.position || null // Position sera assignée par le grid
        };
        
        onSave(widgetInstance);
        onDismiss();
    };

    const tabOptions = availableTabs.map(tab => ({
        key: tab.id,
        text: tab.name
    }));

    // Si aucun tab disponible, afficher un message d'erreur
    if (availableTabs.length === 0) {
        return (
            <Dialog
                hidden={!isOpen}
                onDismiss={onDismiss}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'No Custom Tabs Available'
                }}
                modalProps={{
                    isBlocking: false
                }}
                minWidth={400}
            >
                <Stack tokens={{ childrenGap: 16 }}>
                    <div style={{ color: '#d13438' }}>
                        ⚠️ You need to create a custom tab first before adding widgets.
                    </div>
                    <div>
                        Close this dialog and click "Manage Tabs" to create your first custom tab.
                    </div>
                </Stack>
                <DialogFooter>
                    <DefaultButton onClick={onDismiss} text="Close" />
                </DialogFooter>
            </Dialog>
        );
    }

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            dialogContentProps={{
                type: DialogType.normal,
                title: instance ? 'Edit Widget Instance' : 'Add Widget Instance',
                subText: widgetInfo?.description || ''
            }}
            modalProps={{
                isBlocking: false,
                className: 'widget-instance-dialog'
            }}
            minWidth={500}
        >
            <Stack tokens={{ childrenGap: 16 }}>
                {/* Nom personnalisé */}
                <TextField
                    label="Widget Name"
                    placeholder={widgetInfo?.name}
                    value={config.name}
                    onChange={(e, value) => setConfig({ ...config, name: value })}
                    description="Give this widget instance a custom name"
                />

                {/* Sélection du tab */}
                <Dropdown
                    label="Assign to Tab"
                    selectedKey={config.tabId}
                    onChange={(e, option) => setConfig({ ...config, tabId: option.key })}
                    options={tabOptions}
                    required
                />

                {/* Intervalle de rafraîchissement */}
                <TextField
                    label="Refresh Interval (seconds)"
                    type="number"
                    value={config.refreshInterval.toString()}
                    onChange={(e, value) => setConfig({ ...config, refreshInterval: parseInt(value) || 60 })}
                    min={10}
                    max={3600}
                />

                {/* Options d'affichage */}
                <Stack tokens={{ childrenGap: 8 }}>
                    <Label>Display Options</Label>
                    <Checkbox
                        label="Show title"
                        checked={config.showTitle}
                        onChange={(e, checked) => setConfig({ ...config, showTitle: checked })}
                    />
                    <Checkbox
                        label="Show border"
                        checked={config.showBorder}
                        onChange={(e, checked) => setConfig({ ...config, showBorder: checked })}
                    />
                </Stack>

                {/* Filtres spécifiques (à développer selon le type de widget) */}
                {widgetInfo?.type === 'metric' && (
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Label>Metric Filters</Label>
                        <TextField
                            label="Min Value"
                            type="number"
                            value={config.filters.minValue || ''}
                            onChange={(e, value) => setConfig({ 
                                ...config, 
                                filters: { ...config.filters, minValue: value } 
                            })}
                            placeholder="Optional minimum value"
                        />
                        <TextField
                            label="Max Value"
                            type="number"
                            value={config.filters.maxValue || ''}
                            onChange={(e, value) => setConfig({ 
                                ...config, 
                                filters: { ...config.filters, maxValue: value } 
                            })}
                            placeholder="Optional maximum value"
                        />
                    </Stack>
                )}

                {widgetInfo?.category === 'charts' && (
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Label>Chart Options</Label>
                        <Dropdown
                            label="Time Range"
                            selectedKey={config.filters.timeRange || 'today'}
                            onChange={(e, option) => setConfig({ 
                                ...config, 
                                filters: { ...config.filters, timeRange: option.key } 
                            })}
                            options={[
                                { key: 'today', text: 'Today' },
                                { key: 'yesterday', text: 'Yesterday' },
                                { key: 'last7days', text: 'Last 7 Days' },
                                { key: 'last30days', text: 'Last 30 Days' },
                                { key: 'thisMonth', text: 'This Month' },
                                { key: 'lastMonth', text: 'Last Month' }
                            ]}
                        />
                    </Stack>
                )}
            </Stack>

            <DialogFooter>
                <PrimaryButton onClick={handleSave} text={instance ? 'Update' : 'Add'} />
                <DefaultButton onClick={onDismiss} text="Cancel" />
            </DialogFooter>
        </Dialog>
    );
};

export default WidgetInstanceConfig;
